import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getCustomer,
  CustomerModel,
  updateCustomer,
  UpdateCustomerRequest,
  deleteCustomer,
} from "../../clients/BackOfficeApi";
import { Box, Button, FormControl, FormLabel, Heading, Input, Spinner, Text } from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import { useIsAuthenticated } from "@azure/msal-react";
import {
  ValidationErrorModel,
  isApiError,
  isBadRequest,
} from "../../utils/apiUtils";
import { getDynamicMessageId } from "../../components/I18n";
import { useIntl } from "react-intl";
import { ErrorList } from "../../elements/ErrorList";
import { DetailPageHeader } from "../../compositions/DetailPageHeader";
import { ActionButton } from "../../elements/ActionButton";
import { DeleteIcon } from "@chakra-ui/icons";
import { useDirty } from "../../contexts/DirtyContext";

export function CustomerDetailPage() {
  const customerId = Number(useParams().customerId);
  const [validationError, setValidationError] =
    useState<ValidationErrorModel<UpdateCustomerRequest>>();
  const [error, setError] = useState<string>();
  const isAuthenticated = useIsAuthenticated();
  const intl = useIntl();
  const navigate = useNavigate();
  const [dirty, setDirty] = useDirty(true);

  const [loading, setLoading] = useState(true);

  const [customer, setCustomer] = useState<CustomerModel>();
  const [modifiedCustomer, setModifiedCustomer] =
    useState<UpdateCustomerRequest>();

  const isDirty = useMemo(() => {
    if (!customer || !modifiedCustomer) return false;

    return customer.name !== modifiedCustomer.name;
  }, [customer, modifiedCustomer]);

  useEffect(() => {
    setDirty(isDirty);
  }, [isDirty, setDirty]);

  const fetchCustomer = useCallback(
    async (signal?: AbortSignal) => {
      if (!isAuthenticated) return;

      setLoading(true);

      if (!Number.isInteger(customerId) || customerId <= 0) {
        setError("Ongeldig opdrachtgever ID");
        setLoading(false);
        return;
      }

      getCustomer({
        path: { customerId },
        signal,
        throwOnError: true,
      })
        .then(({ data }) => {
          setCustomer(data);
          setModifiedCustomer((c) => c ?? data);
        })
        .catch((e) => {
          if (signal?.aborted) return;

          setError(`Fout bij ophalen opdrachtgever: ${e.message ?? String(e)}`);
        })
        .finally(() => setLoading(false));
    },
    [isAuthenticated, customerId],
  );

  useEffect(() => {
    const abortController = new AbortController();

    fetchCustomer(abortController.signal);

    return () => abortController.abort("Component unmounted or re-rendered");
  }, [fetchCustomer]);

  const update = useCallback(() => {
    if (!isAuthenticated || !customer || !modifiedCustomer || !dirty) return;

    setError(undefined);
    setValidationError(undefined);

    updateCustomer({
      path: { customerId },
      body: modifiedCustomer,
      throwOnError: true,
    })
      .catch((e) => {
        if (isApiError(e)) {
          const errorId = getDynamicMessageId(e.title ?? "Unknown error");

          setError(
            intl.formatMessage({ id: errorId, defaultMessage: e.title ?? "Unknown error" }),
          );

          if (isBadRequest<UpdateCustomerRequest>(e)) {
            setValidationError(e.errors);
          }
        } else {
          setError(`Fout bij bijwerken opdrachtgever: ${e}`);
        }
      })
      .finally(() => {
        fetchCustomer();
      });
  }, [
    isAuthenticated,
    customer,
    customerId,
    modifiedCustomer,
    dirty,
    fetchCustomer,
    intl,
  ]);

  const fetchDeleteCustomer = useCallback(
    (confirmed: boolean) => {
      if (!confirmed) return;

      deleteCustomer({
        path: { customerId },
        throwOnError: true,
      }).then(() => navigate(".."));
    },
    [navigate, customerId]
  );

  return (
    <Box>
      <DetailPageHeader>
        <ActionButton
          color="red.500"
          confirmDescription="Weet je zeker dat je deze opdrachtgever wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt."
          confirmTitle="Verwijderen bevestigen"
          icon={<DeleteIcon />}
          isDestructive
          onClick={fetchDeleteCustomer}
          title="Verwijderen"
        />
      </DetailPageHeader>
      {loading && <Spinner />}
      {error && <Text color="red.500">{error}</Text>}
      {customer && (
        <Box>
          <Heading>{customer.name}</Heading>
          {modifiedCustomer && (
            <>
              <FormControl isInvalid={!!validationError?.Name} mt={4}>
                <FormLabel htmlFor="customer.name">Naam</FormLabel>
                <Input
                  id="customer.name"
                  name="customer.name"
                  onChange={(e) =>
                    setModifiedCustomer({
                      ...modifiedCustomer,
                      name: e.target.value,
                    })
                  }
                  value={modifiedCustomer.name}
                />
                <ErrorList errors={validationError?.Name} />
              </FormControl>
              <Button isDisabled={!dirty} mt={2} onClick={update}>
                Opslaan
              </Button>
            </>
          )}
        </Box>
      )}
    </Box>
  );
}
